import { IStudentRow } from 'api-paths-cas';
import { IOption } from 'shared-components';
import { ActionSelection } from '../../../types';
import { hasPerm, P } from '@/utilities/roles/permissions';
import { UNI_ROLES, UserRole } from '@/store/auth/types';
import { getActiveScheduledInterviews } from '@/utilities/getActiveScheduledInterviews';
import { getActiveRecordedInterviews } from '@/utilities/getActiveRecordedInterviews';
import { bulkActionGetInstitutionType } from '@/utilities/bulkActions';

export const areOptionsAvailable = (
  userRole: UserRole,
  isCalendlyEnabled: boolean,
  isRecordedInterviewEnabled: boolean,
  isPathwayProvider: boolean,
  selectedRows: string[],
  visibleStudents: IStudentRow[],
  isAddAndCancelInterviewVisible: boolean,
  allStudentsSelected: boolean,
) => {
  if (!isCalendlyEnabled && !isRecordedInterviewEnabled) {
    return false;
  }

  const selectedStudents = visibleStudents?.filter(value => selectedRows.indexOf(value.p_key) > -1) ?? [];

  const isSelectedExactlyOneStudent = selectedStudents.length === 1;
  if (isSelectedExactlyOneStudent) {
    const isPathwayStudent = !!selectedStudents[0]?.pathway;
    if (UNI_ROLES.includes(userRole) && isPathwayStudent && !isPathwayProvider) {
      return isAddAndCancelInterviewVisible;
    }
    return true;
  }

  const studentInstitutionType = bulkActionGetInstitutionType(allStudentsSelected, selectedStudents, isPathwayProvider);
  if (userRole === UserRole.Admin || userRole === UserRole.Enroly || userRole === UserRole.Uni) {
    if (studentInstitutionType === 'pathway' || studentInstitutionType === 'uni') {
      return true;
    }
  }

  if (isPathwayProvider && studentInstitutionType === 'pathway') {
    return true;
  }

  if (!isPathwayProvider && studentInstitutionType === 'uni') {
    return true;
  }

  if (!isPathwayProvider && studentInstitutionType === 'pathway' && UNI_ROLES.includes(userRole)) {
    return isAddAndCancelInterviewVisible;
  }

  return false;
};

export const getScheduledInterviewsOptions = (
  userRole: UserRole,
  isCalendlyEnabled: boolean,
  isRecordedInterviewEnabled: boolean,
  isPathwayProvider: boolean,
  selectedRows: string[],
  visibleStudents: IStudentRow[],
  isAddAndCancelInterviewVisible: boolean,
  allStudentsSelected: boolean,
): Array<IOption> => {
  const options: Array<IOption> = [];

  if (
    !areOptionsAvailable(
      userRole,
      isCalendlyEnabled,
      isRecordedInterviewEnabled,
      isPathwayProvider,
      selectedRows,
      visibleStudents,
      isAddAndCancelInterviewVisible,
      allStudentsSelected,
    )
  ) {
    return options;
  }

  if (hasPerm(userRole, P.SCHEDULED_INTERVIEWS_CANCEL)) {
    const isSelectedExactlyOneStudent = selectedRows.length === 1;

    if (isSelectedExactlyOneStudent) {
      const selectedStudents = visibleStudents?.filter(value => selectedRows.indexOf(value.p_key) > -1) ?? [];
      const activeInterviews = getActiveScheduledInterviews((selectedStudents[0] as any)?.calendlyInterviews ?? []);
      const activeRecordedInterviews = getActiveRecordedInterviews(
        (selectedStudents[0] as any)?.recordedInterviews ?? [],
      );
      if (activeInterviews.length > 0 || activeRecordedInterviews.length > 0) {
        options.push({
          label: 'Cancel interview',
          value: ActionSelection.CANCEL_INTERVIEW,
        });
      }
    } else {
      options.push({
        label: 'Cancel interview',
        value: ActionSelection.CANCEL_INTERVIEW,
      });
    }
  }

  if (hasPerm(userRole, P.SCHEDULED_INTERVIEWS_REQUEST)) {
    options.push({
      label: 'Invite to interview',
      value: ActionSelection.REQUEST_INTERVIEW,
    });
  }

  return options;
};
